/**
 * Selenny Designer — 3D Text Effects
 * Professional text effects using fabric.js IText subclasses.
 * All effects remain fully editable and export at any DPI.
 */
import { fabric } from 'fabric';
import eventBus from './EventBus';

/**
 * Shared helper: Rasterize a custom text effect object to PNG and return
 * an SVG <image> element so the multi-layer _render() output is preserved
 * in SVG exports (Fabric's default toSVG only writes flat text).
 */
function _effectTextToSVG(reviver) {
  // Use natural (unscaled) dimensions — _render() draws at unscaled size
  const pad = (this.padding || 0) + 20;
  const w = this.width + pad * 2;
  const h = this.height + pad * 2;

  // Render to offscreen canvas at natural size
  const tmpCanvas = document.createElement('canvas');
  tmpCanvas.width = Math.ceil(w);
  tmpCanvas.height = Math.ceil(h);
  const tmpCtx = tmpCanvas.getContext('2d');
  tmpCtx.translate(w / 2, h / 2);
  this._render(tmpCtx);

  const dataUrl = tmpCanvas.toDataURL('image/png');

  // getCenterPoint() returns the absolute center regardless of originX/originY
  const center = this.getCenterPoint();
  const angle = this.angle || 0;
  const sx = (this.scaleX || 1) * (this.flipX ? -1 : 1);
  const sy = (this.scaleY || 1) * (this.flipY ? -1 : 1);
  const opacity = typeof this.opacity === 'number' ? this.opacity : 1;

  let markup = `<g transform="translate(${center.x} ${center.y}) rotate(${angle}) scale(${sx} ${sy})"` +
    (opacity < 1 ? ` opacity="${opacity}"` : '') + `>\n` +
    `  <image x="${-(w/2)}" y="${-(h/2)}" width="${Math.ceil(w)}" height="${Math.ceil(h)}" ` +
    `href="${dataUrl}" preserveAspectRatio="none" />\n` +
    `</g>\n`;

  if (reviver) markup = reviver(markup);
  return markup;
}


// ─── 1. 3D EXTRUSION (Comic-Style Depth) ───────────────────────────

fabric.Extrusion3DIText = fabric.util.createClass(fabric.IText, {
  type: 'extrusion3DIText',
  extrusionDepth: 8,
  extrusionColor: '#1a1a2e',
  extrusionAngleX: 2,
  extrusionAngleY: 2,
  editable: false,

  initialize: function (text, options) {
    options = options || {};
    options.editable = false;
    this.extrusionDepth = options.extrusionDepth ?? this.extrusionDepth;
    this.extrusionColor = options.extrusionColor || this.extrusionColor;
    this.extrusionAngleX = options.extrusionAngleX ?? this.extrusionAngleX;
    this.extrusionAngleY = options.extrusionAngleY ?? this.extrusionAngleY;
    this.callSuper('initialize', text, options);
    this.padding = Math.max(this.padding || 0, 10);
  },

  // Prevent Fabric's inline text editing — causes viewport shift on large canvases
  enterEditing: function () { return this; },

  _render: function (ctx) {
    const origFill = this.fill;
    const origStroke = this.stroke;
    const origStrokeWidth = this.strokeWidth;
    const origShadow = this.shadow;

    this.shadow = null;
    this.fill = this.extrusionColor;
    this.stroke = this.extrusionColor;
    this.strokeWidth = 1;

    for (let i = this.extrusionDepth; i > 0; i--) {
      ctx.save();
      ctx.translate(this.extrusionAngleX * i, this.extrusionAngleY * i);
      this.callSuper('_render', ctx);
      ctx.restore();
    }

    this.fill = origFill;
    this.stroke = origStroke;
    this.strokeWidth = origStrokeWidth;
    this.shadow = origShadow;
    this.callSuper('_render', ctx);
  },

  toSVG: _effectTextToSVG,

  toObject: function (props) {
    return fabric.util.object.extend(this.callSuper('toObject', props), {
      extrusionDepth: this.extrusionDepth,
      extrusionColor: this.extrusionColor,
      extrusionAngleX: this.extrusionAngleX,
      extrusionAngleY: this.extrusionAngleY,
    });
  },
});
fabric.Extrusion3DIText.fromObject = function (obj, cb) {
  return fabric.Object._fromObject('Extrusion3DIText', obj, cb, 'text');
};

// ─── 2. OUTLINE STACK (Multiple Colored Outlines) ──────────────────

fabric.OutlineStackIText = fabric.util.createClass(fabric.IText, {
  type: 'outlineStackIText',
  outlines: null,
  editable: false,

  initialize: function (text, options) {
    options = options || {};
    options.editable = false;
    this.outlines = options.outlines || [
      { color: '#000000', width: 12 },
      { color: '#ffffff', width: 6 },
    ];
    this.callSuper('initialize', text, options);
    this.paintFirst = 'stroke';
    this.strokeLineJoin = 'round';
    this.strokeLineCap = 'round';
    this.padding = Math.max(this.padding || 0, (this.outlines[0]?.width || 0) + 4);
  },

  enterEditing: function () { return this; },

  _render: function (ctx) {
    const origFill = this.fill;
    const origStroke = this.stroke;
    const origStrokeWidth = this.strokeWidth;
    const origShadow = this.shadow;

    for (let i = 0; i < this.outlines.length; i++) {
      const ol = this.outlines[i];
      this.fill = null;
      this.stroke = ol.color;
      this.strokeWidth = ol.width;
      this.shadow = i === 0 ? origShadow : null;
      ctx.save();
      this.callSuper('_render', ctx);
      ctx.restore();
    }

    this.fill = origFill;
    this.stroke = origStroke;
    this.strokeWidth = origStrokeWidth;
    this.shadow = null;
    this.callSuper('_render', ctx);
    this.shadow = origShadow;
  },

  toSVG: _effectTextToSVG,

  toObject: function (props) {
    return fabric.util.object.extend(this.callSuper('toObject', props), {
      outlines: this.outlines,
    });
  },
});
fabric.OutlineStackIText.fromObject = function (obj, cb) {
  return fabric.Object._fromObject('OutlineStackIText', obj, cb, 'text');
};

// ─── 3. NEON GLOW ──────────────────────────────────────────────────

fabric.NeonIText = fabric.util.createClass(fabric.IText, {
  type: 'neonIText',
  neonColor: '#00ffff',
  neonIntensity: 3,
  neonBlur: 15,
  editable: false,

  initialize: function (text, options) {
    options = options || {};
    options.editable = false;
    this.neonColor = options.neonColor || this.neonColor;
    this.neonIntensity = options.neonIntensity ?? this.neonIntensity;
    this.neonBlur = options.neonBlur ?? this.neonBlur;
    this.callSuper('initialize', text, options);
    this.padding = Math.max(this.padding || 0, this.neonBlur + 10);
  },

  enterEditing: function () { return this; },

  _render: function (ctx) {
    const origFill = this.fill;
    const origStroke = this.stroke;
    const origStrokeWidth = this.strokeWidth;
    const origShadow = this.shadow;

    this.shadow = null;

    for (let i = this.neonIntensity; i > 0; i--) {
      const blur = this.neonBlur * (i / this.neonIntensity);
      const alpha = 0.15 + 0.25 * (1 - i / this.neonIntensity);

      this.fill = this.neonColor;
      this.stroke = this.neonColor;
      this.strokeWidth = 2 + i * 2;
      this.shadow = new fabric.Shadow({ color: this.neonColor, blur, offsetX: 0, offsetY: 0 });

      ctx.save();
      ctx.globalAlpha *= alpha;
      this.callSuper('_render', ctx);
      ctx.restore();
    }

    this.fill = '#ffffff';
    this.stroke = this.neonColor;
    this.strokeWidth = 1;
    this.shadow = new fabric.Shadow({ color: this.neonColor, blur: 5, offsetX: 0, offsetY: 0 });

    ctx.save();
    this.callSuper('_render', ctx);
    ctx.restore();

    this.fill = origFill;
    this.stroke = origStroke;
    this.strokeWidth = origStrokeWidth;
    this.shadow = origShadow;
  },

  toSVG: _effectTextToSVG,

  toObject: function (props) {
    return fabric.util.object.extend(this.callSuper('toObject', props), {
      neonColor: this.neonColor,
      neonIntensity: this.neonIntensity,
      neonBlur: this.neonBlur,
    });
  },
});
fabric.NeonIText.fromObject = function (obj, cb) {
  return fabric.Object._fromObject('NeonIText', obj, cb, 'text');
};

// ─── 4. RETRO LONG SHADOW ──────────────────────────────────────────

fabric.RetroIText = fabric.util.createClass(fabric.IText, {
  type: 'retroIText',
  longShadowLength: 25,
  longShadowColor: '#2c3e50',
  longShadowAngle: 45,
  editable: false,

  initialize: function (text, options) {
    options = options || {};
    options.editable = false;
    this.longShadowLength = options.longShadowLength ?? this.longShadowLength;
    this.longShadowColor = options.longShadowColor || this.longShadowColor;
    this.longShadowAngle = options.longShadowAngle ?? this.longShadowAngle;
    this.callSuper('initialize', text, options);
    this.padding = Math.max(this.padding || 0, this.longShadowLength + 5);
  },

  enterEditing: function () { return this; },

  _render: function (ctx) {
    const origFill = this.fill;
    const origStroke = this.stroke;
    const origStrokeWidth = this.strokeWidth;
    const origShadow = this.shadow;

    this.shadow = null;
    this.stroke = null;
    this.strokeWidth = 0;

    const rad = this.longShadowAngle * (Math.PI / 180);
    const dx = Math.cos(rad);
    const dy = Math.sin(rad);
    const len = this.longShadowLength;

    // Parse color
    const tc = document.createElement('canvas');
    tc.width = tc.height = 1;
    const tctx = tc.getContext('2d');
    tctx.fillStyle = this.longShadowColor;
    tctx.fillRect(0, 0, 1, 1);
    const px = tctx.getImageData(0, 0, 1, 1).data;

    for (let i = len; i > 0; i--) {
      const a = 0.05 + 0.55 * (1 - i / len);
      this.fill = `rgba(${px[0]},${px[1]},${px[2]},${a})`;
      ctx.save();
      ctx.translate(dx * i, dy * i);
      this.callSuper('_render', ctx);
      ctx.restore();
    }

    this.fill = origFill;
    this.stroke = origStroke;
    this.strokeWidth = origStrokeWidth;
    this.shadow = origShadow;
    this.callSuper('_render', ctx);
  },

  toSVG: _effectTextToSVG,

  toObject: function (props) {
    return fabric.util.object.extend(this.callSuper('toObject', props), {
      longShadowLength: this.longShadowLength,
      longShadowColor: this.longShadowColor,
      longShadowAngle: this.longShadowAngle,
    });
  },
});
fabric.RetroIText.fromObject = function (obj, cb) {
  return fabric.Object._fromObject('RetroIText', obj, cb, 'text');
};

// ─── 5. GOLD / METALLIC GRADIENT (uses native fabric.Gradient) ─────

function createGoldGradient(height) {
  return new fabric.Gradient({
    type: 'linear',
    coords: { x1: 0, y1: 0, x2: 0, y2: height },
    colorStops: [
      { offset: 0, color: '#f5e6a3' },
      { offset: 0.25, color: '#d4a234' },
      { offset: 0.5, color: '#f5ce62' },
      { offset: 0.75, color: '#b8860b' },
      { offset: 1, color: '#d4a234' },
    ],
  });
}

function createChromeGradient(height) {
  return new fabric.Gradient({
    type: 'linear',
    coords: { x1: 0, y1: 0, x2: 0, y2: height },
    colorStops: [
      { offset: 0, color: '#e8e8e8' },
      { offset: 0.3, color: '#888888' },
      { offset: 0.5, color: '#f0f0f0' },
      { offset: 0.7, color: '#666666' },
      { offset: 1, color: '#aaaaaa' },
    ],
  });
}

function createRoseGoldGradient(height) {
  return new fabric.Gradient({
    type: 'linear',
    coords: { x1: 0, y1: 0, x2: 0, y2: height },
    colorStops: [
      { offset: 0, color: '#f7cac9' },
      { offset: 0.3, color: '#e8a09a' },
      { offset: 0.5, color: '#f2c4c2' },
      { offset: 0.75, color: '#c47d78' },
      { offset: 1, color: '#e0a8a3' },
    ],
  });
}

function createYellowOrangeGradient(height) {
  return new fabric.Gradient({
    type: 'linear',
    coords: { x1: 0, y1: 0, x2: 0, y2: height },
    colorStops: [
      { offset: 0, color: '#ffe44d' },
      { offset: 0.4, color: '#ffb830' },
      { offset: 0.7, color: '#ff8c00' },
      { offset: 1, color: '#e65100' },
    ],
  });
}

function createRedYellowGradient(height) {
  return new fabric.Gradient({
    type: 'linear',
    coords: { x1: 0, y1: 0, x2: 0, y2: height },
    colorStops: [
      { offset: 0, color: '#ff4444' },
      { offset: 0.5, color: '#ff6b00' },
      { offset: 1, color: '#ffd700' },
    ],
  });
}

function createBlueGradient(height) {
  return new fabric.Gradient({
    type: 'linear',
    coords: { x1: 0, y1: 0, x2: 0, y2: height },
    colorStops: [
      { offset: 0, color: '#60a5fa' },
      { offset: 0.5, color: '#3b82f6' },
      { offset: 1, color: '#1d4ed8' },
    ],
  });
}

function createGreenGradient(height) {
  return new fabric.Gradient({
    type: 'linear',
    coords: { x1: 0, y1: 0, x2: 0, y2: height },
    colorStops: [
      { offset: 0, color: '#4ade80' },
      { offset: 0.5, color: '#22c55e' },
      { offset: 1, color: '#15803d' },
    ],
  });
}

// ─── PRESET DEFINITIONS ─────────────────────────────────────────────

export const TEXT_3D_PRESETS = [
  {
    id: '3d-comic',
    label: '3D Comic',
    category: '3D',
    preview: { bg: '#ff6b35', color: '#ffffff' },
    create: (text, fontSize, scale) => new fabric.Extrusion3DIText(text, {
      fontSize: Math.round(fontSize * scale),
      fontWeight: '900',
      fontFamily: 'Impact',
      fill: '#ff6b35',
      stroke: '#ffffff',
      strokeWidth: Math.max(2, Math.round(3 * scale)),
      extrusionDepth: Math.max(3, Math.round(6 * scale)),
      extrusionColor: '#8b2500',
      extrusionAngleX: Math.max(1, Math.round(1.5 * scale)),
      extrusionAngleY: Math.max(1, Math.round(1.5 * scale)),
      editable: false,
      paintFirst: 'stroke',
      strokeLineJoin: 'round',
    }),
  },
  {
    id: '3d-block',
    label: '3D Block',
    category: '3D',
    preview: { bg: '#1a56db', color: '#ffffff' },
    create: (text, fontSize, scale) => new fabric.Extrusion3DIText(text, {
      fontSize: Math.round(fontSize * scale),
      fontWeight: 'bold',
      fontFamily: 'Arial Black',
      fill: '#3b82f6',
      stroke: '#1e3a5f',
      strokeWidth: Math.max(1, Math.round(2 * scale)),
      extrusionDepth: Math.max(3, Math.round(6 * scale)),
      extrusionColor: '#1e3a5f',
      extrusionAngleX: Math.max(1, Math.round(2 * scale)),
      extrusionAngleY: Math.max(1, Math.round(1.5 * scale)),
      editable: false,
      paintFirst: 'stroke',
      strokeLineJoin: 'round',
    }),
  },
  {
    id: '3d-party',
    label: 'Party',
    category: '3D',
    preview: { bg: '#e74c3c', color: '#ffd93d' },
    create: (text, fontSize, scale) => new fabric.Extrusion3DIText(text, {
      fontSize: Math.round(fontSize * scale),
      fontWeight: '900',
      fontFamily: 'Luckiest Guy',
      fill: '#ffd93d',
      stroke: '#c0392b',
      strokeWidth: Math.max(2, Math.round(4 * scale)),
      extrusionDepth: Math.max(3, Math.round(5 * scale)),
      extrusionColor: '#7b241c',
      extrusionAngleX: Math.max(1, Math.round(1.5 * scale)),
      extrusionAngleY: Math.max(1, Math.round(2 * scale)),
      editable: false,
      paintFirst: 'stroke',
      strokeLineJoin: 'round',
    }),
  },
  {
    id: 'outline-bold',
    label: 'Outline Bold',
    category: 'Outline',
    preview: { bg: '#e74c3c', color: '#ffffff' },
    create: (text, fontSize, scale) => new fabric.OutlineStackIText(text, {
      fontSize: Math.round(fontSize * scale),
      fontWeight: '900',
      fontFamily: 'Impact',
      fill: '#e74c3c',
      outlines: [
        { color: '#1a1a2e', width: Math.max(8, Math.round(14 * scale)) },
        { color: '#ffffff', width: Math.max(4, Math.round(8 * scale)) },
      ],
      shadow: new fabric.Shadow({ color: 'rgba(0,0,0,0.4)', blur: 8, offsetX: 3, offsetY: 3 }),
      editable: false,
      paintFirst: 'stroke',
      strokeLineJoin: 'round',
    }),
  },
  {
    id: 'outline-sport',
    label: 'Sport',
    category: 'Outline',
    preview: { bg: '#2ecc71', color: '#ffffff' },
    create: (text, fontSize, scale) => new fabric.OutlineStackIText(text, {
      fontSize: Math.round(fontSize * scale),
      fontWeight: '900',
      fontFamily: 'Oswald',
      fill: '#ffffff',
      outlines: [
        { color: '#1a5c2e', width: Math.max(6, Math.round(12 * scale)) },
        { color: '#2ecc71', width: Math.max(3, Math.round(6 * scale)) },
      ],
      editable: false,
      paintFirst: 'stroke',
      strokeLineJoin: 'round',
    }),
  },
  {
    id: 'gold',
    label: 'Gold',
    category: 'Metallic',
    preview: { bg: '#d4a234', color: '#f5e6a3' },
    create: (text, fontSize, scale) => {
      const fs = Math.round(fontSize * scale);
      const obj = new fabric.IText(text, {
        fontSize: fs,
        fontWeight: 'bold',
        fontFamily: 'Georgia',
        paintFirst: 'stroke',
        strokeLineJoin: 'round',
        stroke: '#5a3e1b',
        strokeWidth: Math.max(1, Math.round(2 * scale)),
        shadow: new fabric.Shadow({ color: 'rgba(0,0,0,0.5)', blur: 8, offsetX: 2, offsetY: 4 }),
        editable: false,
      });
      obj.set('fill', createGoldGradient(fs));
      return obj;
    },
  },
  {
    id: 'chrome',
    label: 'Chrom',
    category: 'Metallic',
    preview: { bg: '#888', color: '#e8e8e8' },
    create: (text, fontSize, scale) => {
      const fs = Math.round(fontSize * scale);
      const obj = new fabric.IText(text, {
        fontSize: fs,
        fontWeight: 'bold',
        fontFamily: 'Arial Black',
        paintFirst: 'stroke',
        strokeLineJoin: 'round',
        stroke: '#333333',
        strokeWidth: Math.max(1, Math.round(1 * scale)),
        shadow: new fabric.Shadow({ color: 'rgba(0,0,0,0.6)', blur: 6, offsetX: 1, offsetY: 3 }),
        editable: false,
      });
      obj.set('fill', createChromeGradient(fs));
      return obj;
    },
  },
  {
    id: 'rosegold',
    label: 'Roségold',
    category: 'Metallic',
    preview: { bg: '#c47d78', color: '#f7cac9' },
    create: (text, fontSize, scale) => {
      const fs = Math.round(fontSize * scale);
      const obj = new fabric.IText(text, {
        fontSize: fs,
        fontWeight: 'bold',
        fontFamily: 'Parisienne',
        paintFirst: 'stroke',
        strokeLineJoin: 'round',
        stroke: '#8b5e5a',
        strokeWidth: Math.max(1, Math.round(1 * scale)),
        shadow: new fabric.Shadow({ color: 'rgba(0,0,0,0.3)', blur: 6, offsetX: 1, offsetY: 3 }),
        editable: false,
      });
      obj.set('fill', createRoseGoldGradient(fs));
      return obj;
    },
  },
  {
    id: 'neon-cyan',
    label: 'Neon Cyan',
    category: 'Neon',
    preview: { bg: '#0d1117', color: '#00ffff' },
    create: (text, fontSize, scale) => new fabric.NeonIText(text, {
      fontSize: Math.round(fontSize * scale),
      fontFamily: 'Pacifico',
      fontWeight: 'normal',
      fill: '#00ffff',
      neonColor: '#00ffff',
      neonIntensity: 4,
      neonBlur: Math.round(18 * scale),
      editable: false,
    }),
  },
  {
    id: 'neon-pink',
    label: 'Neon Pink',
    category: 'Neon',
    preview: { bg: '#0d1117', color: '#ff00ff' },
    create: (text, fontSize, scale) => new fabric.NeonIText(text, {
      fontSize: Math.round(fontSize * scale),
      fontFamily: 'Dancing Script',
      fontWeight: 'bold',
      fill: '#ff00ff',
      neonColor: '#ff69b4',
      neonIntensity: 3,
      neonBlur: Math.round(15 * scale),
      editable: false,
    }),
  },
  {
    id: 'neon-green',
    label: 'Neon Grün',
    category: 'Neon',
    preview: { bg: '#0d1117', color: '#39ff14' },
    create: (text, fontSize, scale) => new fabric.NeonIText(text, {
      fontSize: Math.round(fontSize * scale),
      fontFamily: 'Permanent Marker',
      fontWeight: 'normal',
      fill: '#39ff14',
      neonColor: '#39ff14',
      neonIntensity: 3,
      neonBlur: Math.round(15 * scale),
      editable: false,
    }),
  },
  {
    id: 'retro-red',
    label: 'Retro',
    category: 'Retro',
    preview: { bg: '#e74c3c', color: '#ecf0f1' },
    create: (text, fontSize, scale) => new fabric.RetroIText(text, {
      fontSize: Math.round(fontSize * scale),
      fontWeight: 'bold',
      fontFamily: 'Oswald',
      fill: '#e74c3c',
      longShadowLength: Math.round(30 * scale),
      longShadowColor: '#2c3e50',
      longShadowAngle: 45,
      editable: false,
    }),
  },
  {
    id: 'retro-sunrise',
    label: 'Sunrise',
    category: 'Retro',
    preview: { bg: '#f39c12', color: '#2c3e50' },
    create: (text, fontSize, scale) => new fabric.RetroIText(text, {
      fontSize: Math.round(fontSize * scale),
      fontWeight: '900',
      fontFamily: 'Archivo Black',
      fill: '#f39c12',
      longShadowLength: Math.round(20 * scale),
      longShadowColor: '#e74c3c',
      longShadowAngle: 135,
      editable: false,
    }),
  },
  // ─── Gradient + 3D Combos ───
  {
    id: '3d-gradient-fire',
    label: 'Feuer 3D',
    category: 'Gradient 3D',
    preview: { bg: '#1a1a2e', color: '#ff8c00' },
    create: (text, fontSize, scale) => {
      const fs = Math.round(fontSize * scale);
      const obj = new fabric.Extrusion3DIText(text, {
        fontSize: fs,
        fontWeight: '900',
        fontFamily: 'Impact',
        stroke: '#4a1500',
        strokeWidth: Math.max(2, Math.round(3 * scale)),
        extrusionDepth: Math.max(3, Math.round(6 * scale)),
        extrusionColor: '#4a1500',
        extrusionAngleX: Math.max(1, Math.round(1.5 * scale)),
        extrusionAngleY: Math.max(1, Math.round(2 * scale)),
        editable: false,
        paintFirst: 'stroke',
        strokeLineJoin: 'round',
      });
      obj.set('fill', createYellowOrangeGradient(fs));
      return obj;
    },
  },
  {
    id: '3d-gradient-lava',
    label: 'Lava 3D',
    category: 'Gradient 3D',
    preview: { bg: '#1a1a2e', color: '#ff4444' },
    create: (text, fontSize, scale) => {
      const fs = Math.round(fontSize * scale);
      const obj = new fabric.Extrusion3DIText(text, {
        fontSize: fs,
        fontWeight: '900',
        fontFamily: 'Archivo Black',
        stroke: '#3d0000',
        strokeWidth: Math.max(2, Math.round(3 * scale)),
        extrusionDepth: Math.max(3, Math.round(5 * scale)),
        extrusionColor: '#3d0000',
        extrusionAngleX: Math.max(1, Math.round(2 * scale)),
        extrusionAngleY: Math.max(1, Math.round(2 * scale)),
        editable: false,
        paintFirst: 'stroke',
        strokeLineJoin: 'round',
      });
      obj.set('fill', createRedYellowGradient(fs));
      return obj;
    },
  },
  {
    id: '3d-gradient-ocean',
    label: 'Ocean 3D',
    category: 'Gradient 3D',
    preview: { bg: '#0f172a', color: '#60a5fa' },
    create: (text, fontSize, scale) => {
      const fs = Math.round(fontSize * scale);
      const obj = new fabric.Extrusion3DIText(text, {
        fontSize: fs,
        fontWeight: '900',
        fontFamily: 'Oswald',
        stroke: '#1e3a5f',
        strokeWidth: Math.max(2, Math.round(3 * scale)),
        extrusionDepth: Math.max(3, Math.round(5 * scale)),
        extrusionColor: '#0f172a',
        extrusionAngleX: Math.max(1, Math.round(2 * scale)),
        extrusionAngleY: Math.max(1, Math.round(1.5 * scale)),
        editable: false,
        paintFirst: 'stroke',
        strokeLineJoin: 'round',
      });
      obj.set('fill', createBlueGradient(fs));
      return obj;
    },
  },
  {
    id: '3d-gradient-nature',
    label: 'Nature 3D',
    category: 'Gradient 3D',
    preview: { bg: '#14532d', color: '#4ade80' },
    create: (text, fontSize, scale) => {
      const fs = Math.round(fontSize * scale);
      const obj = new fabric.Extrusion3DIText(text, {
        fontSize: fs,
        fontWeight: '900',
        fontFamily: 'Luckiest Guy',
        stroke: '#14532d',
        strokeWidth: Math.max(2, Math.round(3 * scale)),
        extrusionDepth: Math.max(3, Math.round(5 * scale)),
        extrusionColor: '#14532d',
        extrusionAngleX: Math.max(1, Math.round(1.5 * scale)),
        extrusionAngleY: Math.max(1, Math.round(2 * scale)),
        editable: false,
        paintFirst: 'stroke',
        strokeLineJoin: 'round',
      });
      obj.set('fill', createGreenGradient(fs));
      return obj;
    },
  },
  {
    id: '3d-gold-extrusion',
    label: 'Gold 3D',
    category: 'Gradient 3D',
    preview: { bg: '#1a1a2e', color: '#f5ce62' },
    create: (text, fontSize, scale) => {
      const fs = Math.round(fontSize * scale);
      const obj = new fabric.Extrusion3DIText(text, {
        fontSize: fs,
        fontWeight: 'bold',
        fontFamily: 'Georgia',
        stroke: '#5a3e1b',
        strokeWidth: Math.max(1, Math.round(2 * scale)),
        extrusionDepth: Math.max(3, Math.round(5 * scale)),
        extrusionColor: '#3d2800',
        extrusionAngleX: Math.max(1, Math.round(1.5 * scale)),
        extrusionAngleY: Math.max(1, Math.round(2 * scale)),
        editable: false,
        paintFirst: 'stroke',
        strokeLineJoin: 'round',
        shadow: new fabric.Shadow({ color: 'rgba(0,0,0,0.4)', blur: 8, offsetX: 2, offsetY: 4 }),
      });
      obj.set('fill', createGoldGradient(fs));
      return obj;
    },
  },
];

export { createGoldGradient, createChromeGradient, createRoseGoldGradient, createYellowOrangeGradient, createRedYellowGradient, createBlueGradient, createGreenGradient };
